
import React, { useEffect, useState } from 'react';
import {
    Phone, Mail, Clock, Send, MessageCircle,
    Calendar, Star, ArrowRight, Globe, Users, Award
} from 'lucide-react';
import Footer from '../components/Footer';

const Contact = () => {
    const [scrollY, setScrollY] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
    });
    const [formErrors, setFormErrors] = useState({
        name: '',
        email: '',
        message: '',
        company: '',
        service: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const services = [
        'HR Consulting', 'Recruitment Services', 'Training & Development',
        'Performance Management', 'Compliance Support',
        'Organizational Development', 'Employee Engagement', 'Strategic Planning'
    ];

    const contactMethods = [
        {
            title: 'Phone Support',
            desc: 'Speak directly with our HR experts for immediate assistance and personalized guidance.',
            icon: Phone,
            contact: '+91 72761 56055',
            contactPerson: 'Navam Team',
            action: 'Call Now',
            actionLink: 'tel:+917276156055',
            color: 'from-green-500 to-emerald-500',
            availability: 'Mon-Sat, 9AM-7PM'
        },
        {
            title: 'Email Us',
            desc: 'Send detailed inquiries and receive comprehensive responses within 48 hours.',
            icon: Mail,
            contact: 'info@navambizsolutions.com',
            contactPerson: 'General Inquiries',
            action: 'Send Email',
            actionLink: 'mailto:info@navambizsolutions.com',
            color: 'from-blue-500 to-indigo-500',
            availability: 'Earliest Response'
        }
    ];

    const whyChooseUs = [
        { icon: Users, title: 'Expert Team', desc: 'Seasoned HR professionals with diverse industry experience' },
        { icon: Award, title: 'Proven Results', desc: '95% client satisfaction rate with measurable business outcomes' },
        { icon: Globe, title: 'Comprehensive Solutions', desc: 'End-to-end HR services tailored to your specific business needs' }
    ];

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
            document.querySelectorAll('.fade-in').forEach((el, i) => {
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight * 0.8) {
                    setTimeout(() => {
                        el.classList.add('animate-in');
                    }, i * 100);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Validate fields
        if (name === 'message') {
            const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
            setFormErrors({
                ...formErrors,
                message: wordCount < 2 && value.trim() ? 'Please enter at least 2 words' : '',
            });
        } else if (name === 'name') {
            setFormErrors({
                ...formErrors,
                name: value.trim() ? '' : 'Name is required',
            });
        } else if (name === 'email') {
            setFormErrors({
                ...formErrors,
                email: value.trim() ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email format') : 'Email is required',
            });
        } else if (name === 'company') {
            setFormErrors({
                ...formErrors,
                company: value.trim() ? '' : 'Company name is required',
            });
        } else if (name === 'service') {
            setFormErrors({
                ...formErrors,
                service: value.trim() ? '' : 'Service is required',
            });
        } else if (name === 'phone') {
            setFormErrors({
                ...formErrors,
                phone: value.trim() && !/^\+?[\d\s-]{7,}$/.test(value) ? 'Invalid phone number' : '',
            });
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        // Validation
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name is required';
        if (!formData.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
        if (formData.phone.trim() && !/^\+?[\d\s-]{7,}$/.test(formData.phone)) errors.phone = 'Invalid phone number';
        const wordCount = formData.message.trim().split(/\s+/).filter(Boolean).length;
        if (!formData.message.trim()) errors.message = 'Message is required';
        else if (wordCount < 2) errors.message = 'Please enter at least 2 words';
        if (!formData.company.trim()) errors.company = 'Company name is required';
        if (!formData.service.trim()) errors.service = 'Service is required';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setLoading(true);
        setError(null);
        setFormErrors({ name: '', email: '', message: '', company: '', service: '', phone: '' });

        const backendUrl = 'https://navam-web-server.onrender.com/api/send_email';
        const apiKey = process.env.REACT_APP_API_KEY;

        try {
            const response = await fetch(backendUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`, // Updated to include Bearer prefix
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone || null,
                    company_name: formData.company,
                    specific_service: formData.service,
                    message: formData.message,
                    type: 'specificService',
                }),
                signal: AbortSignal.timeout(60000), // Timeout for Render spin-down
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit form');
            }

            setSubmitted(true);
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                service: '',
                message: ''
            });
        } catch (err) {
            setError(err.name === 'TimeoutError' ? 'Request timed out. Please try again.' : err.message || 'Something went wrong.');
            console.error('Submission error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 -left-20 w-96 h-96 bg-[#0e6d87]/5 rounded-full blur-3xl" style={{ transform: `translateY(${scrollY * 0.2}px)` }} />
                <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" style={{ transform: `translateY(${scrollY * -0.3}px)` }} />
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/3 rounded-full blur-3xl" style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.1}px)` }} />
            </div>

            <section className="relative py-16 px-4 sm:px-6 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    {/* Hero */}
                    <div className="text-center mb-20 fade-in">
                        <span className="inline-block px-4 py-2 bg-[#0e6d87]/10 text-[#0e6d87] rounded-full text-sm font-medium mb-6">
                            Let's Transform Your HR Together
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-8">
                            <span className="bg-gradient-to-r from-[#0e6d87] via-blue-600 to-gray-700 bg-clip-text text-transparent">Get in Touch</span><br />
                            <span className="bg-gradient-to-r from-gray-700 via-[#0e6d87] to-blue-600 bg-clip-text text-transparent">With Our Experts</span>
                        </h1>
                        <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                            Ready to revolutionize your HR strategy? <span className="text-[#0e6d87] font-semibold">Connect with us</span> today and discover how we can <span className="text-[#0e6d87] font-semibold">empower your business</span> for sustainable growth.
                        </p>
                    </div>

                    {/* Contact Methods */}
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-8 mb-20 sm:mb-24 max-w-4xl mx-auto min-h-[400px] sm:min-h-[300px]">
                        {contactMethods.map((method, i) => {
                            const Icon = method.icon;
                            return (
                                <div
                                    key={i}
                                    className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 fade-in cursor-pointer w-full max-w-md mx-auto flex flex-col items-center"
                                >
                                    <div
                                        className={`w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-r ${method.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                                    >
                                        <Icon className="w-6 sm:w-7 h-6 sm:h-7 text-white animate-pulse-slow" />
                                    </div>
                                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#0e6d87] transition-colors duration-300 text-center">
                                        {method.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm sm:text-base mb-4 leading-relaxed text-center">{method.desc}</p>
                                    <div className="space-y-2 mb-6 text-center">
                                        <a
                                            href={method.actionLink}
                                            className="text-[#0e6d87] font-semibold text-sm sm:text-base hover:underline"
                                        >
                                            {method.contact}
                                        </a>
                                        <p className="text-gray-500 text-xs sm:text-sm">{method.contactPerson}</p>
                                        <div className="flex items-center justify-center text-gray-500 text-xs sm:text-sm">
                                            <Clock className="w-3 sm:w-4 h-3 sm:h-4 mr-1" />
                                            {method.availability}
                                        </div>
                                    </div>
                                    <a
                                        href={method.actionLink}
                                        className="flex items-center justify-center text-[#0e6d87] font-medium text-sm sm:text-base group-hover:translate-x-2 transition-transform duration-300 hover:underline"
                                    >
                                        <span>{method.action}</span>
                                        <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2" />
                                    </a>
                                </div>
                            );
                        })}
                    </div>

                    {/* Contact Form + Why Us */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-24">
                        <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-8 lg:p-12 shadow-xl border border-white/20 fade-in">
                            <div className="flex items-center mb-8">
                                <div className="w-12 h-12 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-xl flex items-center justify-center mr-4">
                                    <MessageCircle className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Send Us a Message</h2>
                                    <p className="text-gray-600">We'll get back to you within 24 hours</p>
                                </div>
                            </div>

                            {submitted ? (
                                <div className="text-center text-green-600 font-medium text-lg">
                                    <div className="flex items-center justify-center mb-4">
                                        <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    Email sent successfully! Our team will connect with you soon.
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                                placeholder="Enter your full name"
                                                required
                                            />
                                            {formErrors.name && (
                                                <p className="text-red-600 text-sm mt-1">{formErrors.name}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                                placeholder="Enter your email"
                                                required
                                            />
                                            {formErrors.email && (
                                                <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                                placeholder="+91 XXXXX XXXXX"
                                            />
                                            {formErrors.phone && (
                                                <p className="text-red-600 text-sm mt-1">{formErrors.phone}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                                placeholder="Your company name"
                                                required
                                            />
                                            {formErrors.company && (
                                                <p className="text-red-600 text-sm mt-1">{formErrors.company}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In *</label>
                                        <select
                                            name="service"
                                            value={formData.service}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                            required
                                        >
                                            <option value="">Select a service</option>
                                            {services.map((s, i) => <option key={i} value={s}>{s}</option>)}
                                        </select>
                                        {formErrors.service && (
                                            <p className="text-red-600 text-sm mt-1">{formErrors.service}</p>
                                        )}
                                        {formData.service && (
                                            <p className="text-gray-600 text-sm mt-2">Selected Service: {formData.service}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows="4"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#0e6d87] transition-all duration-300"
                                            placeholder="Tell us more about your needs..."
                                            required
                                        />
                                        {formErrors.message && (
                                            <p className="text-red-600 text-sm mt-1">{formErrors.message}</p>
                                        )}
                                    </div>

                                    {error && (
                                        <div className="text-red-600 text-center text-sm">{error}</div>
                                    )}

                                    <button
                                        type="submit"
                                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#0e6d87] to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-70"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Submitting...
                                            </span>
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5 mr-2" />
                                                Submit Inquiry
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6 fade-in">
                            <div className="bg-gradient-to-br from-[#0e6d87]/5 to-blue-50 rounded-2xl p-6 lg:p-8 border border-[#0e6d87]/10">
                                <h3 className="text-xl font-bold text-gray-900 mb-6">Why Choose Navam?</h3>
                                {whyChooseUs.map((item, i) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={i} className="flex items-start mb-4">
                                            <div className="w-10 h-10 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                                <Icon className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{item.title}</h4>
                                                <p className="text-sm text-gray-600">{item.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
                .fade-in {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: all 0.8s ease;
                }
                .animate-in {
                    opacity: 1;
                    transform: translateY(0);
                }
                @keyframes pulseSlow {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.1);
                    }
                }
                .animate-pulse-slow {
                    animation: pulseSlow 2s infinite ease-in-out;
                }
            `}</style>
            <Footer />
        </div>
    );
};

export default Contact;